import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Helmet } from 'react-helmet-async';

const QuickCommerceServices = () => {
  const platforms = [
    {name: 'Blinkit', color: 'from-yellow-400 to-orange-400', services: ['Seller Onboarding', 'Account Management & Optimization']},
    {name: 'Swiggy Instamart', color: 'from-orange-500 to-red-500', services: ['Seller Onboarding', 'Account Management Services']},
    {name: 'Zomato', color: 'from-red-500 to-pink-500', services: ['Seller Onboarding Services', 'Marketplace Account Management']},
    {name: 'Swiggy', color: 'from-orange-600 to-orange-400', services: ['Seller Onboarding Services', 'Account Management & Growth']}
  ];

  return (
    <div className="min-h-screen pt-24">
      <Helmet>
        <title>E-commerce & Quick Commerce Services India | Obiquiste Solutions</title>
        <meta name="description" content="Complete marketplace services: Amazon, Flipkart, Myntra, Ajio, Blinkit, Swiggy Instamart, Zomato. Account management, listing, ads & seller onboarding." />
        <link rel="canonical" href="https://www.obiquistesolutions.com/quick-commerce-account-management" />
      </Helmet>
      <div className="bg-slate-50 py-4">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="flex items-center space-x-2 text-sm text-slate-600">
            <Link to="/" className="hover:text-primary">Home</Link>
            <ChevronRight size={16} />
            <Link to="/services" className="hover:text-primary">Services</Link>
            <ChevronRight size={16} />
            <span className="text-slate-900 font-medium">Quick Commerce Services</span>
          </div>
        </div>
      </div>

      <section className="py-16 md:py-24 bg-gradient-to-br from-orange-50 to-red-50/30">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="text-center max-w-4xl mx-auto">
            <h1 className="font-heading text-5xl md:text-7xl font-bold tracking-tight mb-6">
              Quick Commerce <span className="bg-gradient-to-r from-orange-500 to-red-500 bg-clip-text text-transparent">Account Management</span>
            </h1>
            <p className="text-lg md:text-xl leading-relaxed text-slate-600 mb-8">
              Professional quick commerce management for Blinkit, Swiggy Instamart, Zomato, and Swiggy platforms.
            </p>
            <Link to="/contact?service=quick-commerce">
              <Button className="bg-primary text-white hover:bg-primary-600 px-8 py-6 rounded-full">
                Get Started <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {platforms.map((platform, idx) => (
              <motion.div key={idx} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-white p-8 rounded-2xl shadow-md border border-slate-200">
                <h3 className={`font-heading text-3xl font-bold mb-6 bg-gradient-to-r ${platform.color} bg-clip-text text-transparent`}>{platform.name}</h3>
                <ul className="space-y-3 mb-6">
                  {platform.services.map((service, sIdx) => (
                    <li key={sIdx} className="flex items-center text-slate-700">
                      <span className="text-primary mr-2">✓</span> {service}
                    </li>
                  ))}
                </ul>
                <Link to="/contact?service=quick-commerce">
                  <Button variant="outline" className="w-full">Get Started</Button>
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-slate-900 text-white">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl text-center">
          <h2 className="font-heading text-4xl font-bold mb-6">Ready to Enter Quick Commerce?</h2>
          <p className="text-lg text-slate-300 mb-8">Get expert guidance on quick commerce platform onboarding and management.</p>
          <Link to="/contact?service=quick-commerce">
            <Button className="bg-primary text-white hover:bg-primary-600 px-8 py-6 rounded-full">Request Consultation</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default QuickCommerceServices;
